import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AuthContext } from '../AuthContext';
import axiosInstance from '../axiosConfig';
import { OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './OTPVerification.css'

function OTPVerification() {
    const [otp, setOtp] = useState('');
    const { verifyOtp, setOtpRequired, authTokens, logoutUser, user } = useContext(AuthContext);
    const [resendTimer, setResendTimer] = useState(60); // Таймер по умолчанию установлен на 60 секунд
    const [resendMessage, setResendMessage] = useState('');
    const [canResend, setCanResend] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [shakeButton, setShakeButton] = useState(false);
    const [isResending, setIsResending] = useState(false); // По умолчанию false

    const navigate = useNavigate();

    // Функция отправки OTP, обернутая в useCallback
    const handleSubmit = useCallback(async (otpCode) => {
        try {
            const success = await verifyOtp(otpCode);
            if (!success) {
                setErrorMessage('Неверный код или он устарел. Пожалуйста, попробуйте снова.');
                setShakeButton(true);
                setTimeout(() => setShakeButton(false), 500);
            } else {
                setOtpRequired(false);
                if (user?.selectedServiceCenter) {
                    navigate('/home');
                } else {
                    navigate('/select-service-center');
                }
            }
        } catch (error) {
            console.error('Ошибка при верификации OTP:', error);
            setErrorMessage('Произошла ошибка при верификации. Пожалуйста, попробуйте снова.');
            setShakeButton(true);
            setTimeout(() => setShakeButton(false), 500);
        }
    }, [verifyOtp, setOtpRequired, user, navigate]);

    // Проверка токена при загрузке компонента
    useEffect(() => {
        if (!authTokens) {
            navigate('/login');
        }
    }, [authTokens, navigate]);

    // Проверка и установка таймера при загрузке компонента
    useEffect(() => {
        const lastSentTime = localStorage.getItem('otpLastSentTime');
        if (lastSentTime) {
            const timeSinceLastSent = Math.floor((Date.now() - parseInt(lastSentTime, 10)) / 1000);
            if (timeSinceLastSent < 60) {
                setResendTimer(60 - timeSinceLastSent);
                setCanResend(false);
            } else {
                setCanResend(true);
            }
        } else {
            setCanResend(true); // Если это первый запуск и отправки не было
        }
    }, []);

    // Таймер для обратного отсчета
    useEffect(() => {
        let timerId;
        if (resendTimer > 0) {
            timerId = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
        } else {
            setCanResend(true);
        }
        return () => clearTimeout(timerId); // Очистка таймера при размонтировании
    }, [resendTimer]);

    // Обработчик автоматической отправки OTP при вводе 6 цифр
    useEffect(() => {
        if (otp.length === 6) {
            handleSubmit(otp);
        }
    }, [otp, handleSubmit]);

    // Функция повторной отправки OTP
    const handleResend = async () => {
        setErrorMessage('');
        setIsResending(true); // Включаем спиннер
        if (!authTokens || !authTokens.access) {
            setResendMessage('Токен недоступен. Пожалуйста, авторизуйтесь заново.');
            logoutUser();
            setIsResending(false);
            return;
        }

        try {
            const response = await axiosInstance.post('/api/send-otp/', {}, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                },
            });

            if (response.data.success) {
                const currentTime = Date.now();
                localStorage.setItem('otpLastSentTime', currentTime.toString());
                setResendMessage('Код был повторно отправлен');
                setResendTimer(60); // Устанавливаем таймер на 60 секунд
                setCanResend(false);
            } else {
                setResendMessage('Ошибка при повторной отправке кода');
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logoutUser();
            }
            setResendMessage('Ошибка сети. Пожалуйста, попробуйте позже.');
            console.error('Ошибка при повторной отправке OTP:', error);
        } finally {
            setIsResending(false); // Отключаем спиннер после завершения запроса
        }
    };

    // Обработчик изменения ввода, который разрешает только цифры
    const handleInputChange = (e) => {
        const input = e.target.value;
        if (/^\d*$/.test(input)) {
            setOtp(input);
        }
    };

    return (
        <div className="otp-container">
            <h2>Введите код подтверждения</h2>
            <p className="otp-instruction">
                Код был отправлен вам в Telegram. Пожалуйста, введите его ниже:
            </p>
            <form onSubmit={(e) => e.preventDefault()}>
                <input
                    type="text"
                    value={otp}
                    onChange={handleInputChange}
                    className="otp-input"
                    placeholder="Введите код"
                    required
                    inputMode="numeric"
                    pattern="\d*"
                />
            </form>
            {errorMessage && <p className="error-text" style={{ color: 'red' }}>{errorMessage}</p>}
            <div className="button-group mt-3 cart-componetn">
                {canResend ? (
                    <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="tooltip-resend">Запросить код</Tooltip>}
                    >
                        <button 
                            onClick={handleResend} 
                            className={`btn btn-primary ml-2 ${shakeButton ? 'shake' : ''}`}
                            disabled={isResending} // Отключаем кнопку при отправке
                        >
                            {isResending ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <i className="fas fa-paper-plane"></i>}
                        </button>
                    </OverlayTrigger>
                ) : (
                    <p className="timer-text">Повторно запросить код можно через {resendTimer} секунд(ы)</p>
                )}
                
                <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id="tooltip-logout">Выйти</Tooltip>}
                >
                    <button onClick={logoutUser} className="btn btn-danger ml-2">
                        <i className="fas fa-sign-out-alt"></i>
                    </button>
                </OverlayTrigger>
            </div>
            {resendMessage && <p className="resend-message">{resendMessage}</p>}
            <p className="help-text">
                Если вам не приходит код, <br/> пройдите авторизацию через{' '}
                <a href="https://t.me/SecureAuthHelperBot" target="_blank" rel="noopener noreferrer">
                    SecureAuthBot
                </a>.
            </p>
        </div>
    );
}

export default OTPVerification;
